import { h, createContext, FunctionalComponent } from 'preact';
import { initialViolations, ViolationRule, Violations } from 'constants/violations';
import { useContext, useEffect, useState } from 'preact/hooks';
import { api } from 'api';
import { isMobile } from 'helpers/detect-user-agent';
import { ApplicationContext } from './use-application-setup';

interface ViolationsContextType {
  fetchingViolationsInProgress: boolean;
  foundViolations: Violations;
  fixedViolations: Violations;
  violationRules: ViolationRule[];
}

const initialViolationsContext: ViolationsContextType = {
  fetchingViolationsInProgress: true,
  foundViolations: initialViolations,
  fixedViolations: initialViolations,
  violationRules: [],
};

export const ViolationsContext = createContext<ViolationsContextType>(initialViolationsContext);

export const ViolationsProvider: FunctionalComponent = ({ children }) => {
  // Same `a11y-data/v0/site/.../stats` GET as Angular `ScanModel.loadRemediations` (dashboard-violations.js).
  // Preact loads stats when `new_manage` or `isLAUser` (see ManageInterface); legacy Angular manage skips Preact UI but still mounts this provider — do not fetch when Angular owns stats.
  const { siteUrl, paidProductsEnabled, tuningsFromServerInProgress, tunings } =
    useContext(ApplicationContext);

  const preactOwnsManageViolations = !!(tunings?.new_manage || tunings?.isLAUser);

  const [fetchingViolationsInProgress, setFetchingViolationsInProgress] = useState(
    initialViolationsContext.fetchingViolationsInProgress,
  );
  const [foundViolations, setFoundViolations] = useState<Violations>(
    initialViolationsContext.foundViolations,
  );
  const [fixedViolations, setFixedViolations] = useState<Violations>(
    initialViolationsContext.fixedViolations,
  );
  const [violationRules, setViolationRules] = useState<ViolationRule[]>(
    initialViolationsContext.violationRules,
  );

  useEffect(() => {
    if (!siteUrl || tuningsFromServerInProgress || !tunings) {
      return;
    }

    // Skip violations request when strict privacy mode is enabled
    if (tunings.strict_privacy_mode || !preactOwnsManageViolations) {
      setFetchingViolationsInProgress(false);
      return;
    }

    (async () => {
      try {
        const response = await api.get(`a11y-data/v0/site/${siteUrl}/stats`);
        const data = response.payload;
        if (!data) {
          return;
        }

        // search for violations context id
        const device = isMobile() ? 'MOBILE' : 'DESKTOP';
        const widgetState = paidProductsEnabled.REMEDIATION ? 'WIDGET_ON' : 'WIDGET_OFF';
        const contextKey = data.contexts.find(
          (e) => e.device === device && e.aiWidget === widgetState,
        )?.scanContextId;
        const context = data.contextStats[contextKey];

        // collects violation ruleset
        const rules = {};
        data.rules.forEach((rule) => {
          rules[rule.id] = rule;
        });
        const ruleset = context.ruleStats
          .map((rule) => ({
            count: rule.violationsNum,
            title: rules[rule.ruleId].failed,
            level: rules[rule.ruleId].level,
          }))
          .sort((a, b) => (a.count > b.count ? -1 : 1));
        setViolationRules(ruleset);

        // collect data
        setFoundViolations({
          totalCount: context.foundViolationsNum,
          A: {
            count: context.foundViolationsANum,
          },
          AA: {
            count: context.foundViolationsAANum,
          },
          AAA: {
            count: context.foundViolationsAAANum,
          },
        });
        setFixedViolations({
          totalCount: context.fixedViolationsNum,
          A: {
            count: context.fixedViolationsANum,
          },
          AA: {
            count: context.fixedViolationsAANum,
          },
          AAA: {
            count: context.fixedViolationsAAANum,
          },
        });
      } catch (err) {
        console.error(err);
      } finally {
        setFetchingViolationsInProgress(false);
      }
    })();
  }, [siteUrl, tuningsFromServerInProgress, tunings]);

  return (
    <ViolationsContext.Provider
      value={{ fetchingViolationsInProgress, foundViolations, fixedViolations, violationRules }}
    >
      {children}
    </ViolationsContext.Provider>
  );
};

export const useViolations = () => useContext(ViolationsContext);
